import { ThemeProvider as EmotionThemeProvider, Global, css } from '@emotion/react';

import { productionTheme } from './index';
import type { Theme } from './index';
import type { ReactNode } from 'react';

export interface ThemeProviderProps {
  theme?: Theme;
  children: ReactNode;
}

/**
 * Creates CSS custom properties from the theme for CSS module compatibility.
 * CSS modules can use var(--color-primary) to access dynamic theme values.
 */
const createCssVariables = (theme: Theme) => css`
  :root {
    /* Dynamic tenant colors (from login API) */
    --color-primary: ${theme.colors.primary};
    --color-primary-text: ${theme.colors.primaryText};
    --color-secondary: ${theme.colors.secondary};
    --color-secondary-text: ${theme.colors.secondaryText};
    --color-highlight: ${theme.colors.highlight};
    --color-highlight-text: ${theme.colors.highlightText};
    
    /* Fixed text colors */
    --color-text-primary: ${theme.colors.text.primary};
    --color-text-secondary: ${theme.colors.text.secondary};
    --color-text-muted: ${theme.colors.text.muted};
    --color-text-inverse: ${theme.colors.text.inverse};
    
    /* Fixed status colors */
    --color-success: ${theme.colors.status.success};
    --color-error: ${theme.colors.status.error};
    --color-warning: ${theme.colors.status.warning};
    --color-info: ${theme.colors.status.info};
    
    /* Fixed background colors */
    --color-background: ${theme.colors.background.default};
    --color-background-hover: ${theme.colors.background.hover};
    --color-background-active: ${theme.colors.background.active};
    --color-background-disabled: ${theme.colors.background.disabled};
    
    /* Fixed border colors */
    --color-border-light: ${theme.colors.border.light};
    --color-border-medium: ${theme.colors.border.medium};
    --color-border-input: ${theme.colors.border.input};
    --color-border-dotted: ${theme.colors.border.dotted};
    
    /* Grey scale */
    --color-grey-100: ${theme.colors.grey[100]};
    --color-grey-200: ${theme.colors.grey[200]};
    --color-grey-300: ${theme.colors.grey[300]};
    --color-grey-400: ${theme.colors.grey[400]};
    --color-grey-500: ${theme.colors.grey[500]};
    --color-grey-600: ${theme.colors.grey[600]};
    --color-grey-700: ${theme.colors.grey[700]};
    --color-grey-800: ${theme.colors.grey[800]};
    --color-grey-900: ${theme.colors.grey[900]};
    
    /* Common aliases */
    --color-white: #ffffff;
    --color-black: #000000;
  }
`;

export const ThemeProvider = ({
  theme = productionTheme,
  children,
}: ThemeProviderProps) => (
  <EmotionThemeProvider theme={theme}>
    <Global styles={createCssVariables(theme)} />
    {children}
  </EmotionThemeProvider>
);